import React, { useContext, useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { del, get, put } from 'helpers/api_helper'
import ListenerComponent from 'components/Common/Socket/ListenerComponent'
import { store as socketStore } from 'components/Common/Socket/store'
import useConfirmedOperation from 'components/Common/useConfirmedOperation'
import { competitionIdentity } from './CompetitionIdentity'
import { formatEventDate } from '../../../EventPresentation'
import useXlsxDownload from '../Logistics/useXlsxDownload'

// RELEASE-TENIS10 § 11 P92 (Tudor 2026-10-26, round 2; canvas R2 · 3, CP2-toolbar): the Tenis10 tournament programme — one PDF for the
// whole event, on the event's page and on every competition's — is off until it is published, here or in SportyaOS, at any time; it can
// be withdrawn again. Its control sits beside „+ Add competition” and its state under the heading; the competition list stays as it is.
// `children({ actions, status })` places them; an event without a programme (an adult one) gets neither.

const PROGRAM_FAILED = 'The programme could not be downloaded. Try again.'
const timeout = { timeout: 15000 }
export const programUrl = eventId => `/exports/events/${eventId}/program`
const REFUSALS = {
  FORBIDDEN: 'Your account cannot change this event’s programme.',
  ERROR_PERMISSIONS: 'You cannot change this event’s programme.',
  INVALID_PROGRAM_EVENT: 'Only a Tenis10 event has a tournament programme.',
  EVENT_NOT_FOUND: 'The event no longer exists.',
  PROGRAM_PUBLICATION_UNCONFIRMED: 'The change could not be confirmed. Check status before trying again.',
}

// „Friday 30 October”: the day the site promises the programme by (the api's `deadline`, a local date of the event).
const deadlineDay = deadline => {
  const day = deadline ? new Date(`${deadline}T00:00:00.000Z`) : null
  return day && !Number.isNaN(day.getTime())
    ? new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }).format(day).replace(',', '') : null
}
// The live Tenis10 competitions whose Main Draw is not generated yet: published anyway, their groups join after the draw.
const withoutDraw = competitions => (competitions || [])
  .filter(c => c.tenis10Level && !['DELETED', 'DISABLED'].includes(c.status) && c.drawStatus?.mainDraw !== true)
  .map(c => { const { levelLabel, markers } = competitionIdentity(c); return [levelLabel, ...markers.map(m => m.label)].join(' ') })
const competitionCount = count => `${count} ${count === 1 ? 'competition has' : 'competitions have'}`

export default function ProgramPublication({ eventId, program, competitions, timezone, onChanged, children }) {
  const { state: { socket: { socketClientId } = {} } = {} } = useContext(socketStore)
  const operation = useConfirmedOperation()
  const pdf = useXlsxDownload(PROGRAM_FAILED)
  const [confirm, setConfirm] = useState(null)
  if (!program) return children({ actions: null, status: null })

  const published = Boolean(program.publishedAt)
  const missing = withoutDraw(competitions)
  const run = publish => {
    setConfirm(null)
    if (operation.busy || !socketClientId) return
    operation.run({
      execute: () => (publish ? put : del)(`/admin/events/${eventId}/program-publication`, { socketClientId }, timeout),
      // Events answers with the event it changed: never another event's publication.
      matchesSocket: payload => payload?.operation === 'event-program-publication' && payload?.eventId === eventId,
      refusals: REFUSALS,
      read: () => get(`/admin/events/${eventId}/competition-directory`),
      isComplete: response => Boolean(response?.data?.program?.publishedAt) === publish,
      onSuccess: () => onChanged?.(),
    })
  }
  const busy = operation.busy || !socketClientId
  const download = () => pdf.download(programUrl(eventId), {}, 'tournament-programme.pdf')
  const actions = published
    ? <>
      <Button color="primary" outline disabled={pdf.busy} onClick={download}>{pdf.busy ? 'Generating PDF...' : 'Programme (PDF)'}</Button>
      <Button color="danger" outline disabled={busy} onClick={() => setConfirm('unpublish')}>Unpublish programme</Button>
    </>
    : <>
      <Button color="primary" outline disabled={pdf.busy} onClick={download}>{pdf.busy ? 'Generating PDF...' : 'Preview programme (PDF)'}</Button>
      <Button color="primary" disabled={busy} onClick={() => setConfirm('publish')}>Publish programme</Button>
    </>
  const missingText = missing.length ? `${competitionCount(missing.length)} no draw yet: ${missing.join(', ')}` : ''
  const deadline = deadlineDay(program.deadline)
  const status = <div className={'program-publication alert ' + (published ? 'alert-success' : 'alert-warning')} role="status">
    {published
      ? <p className="mb-0"><small className="program-publication-state">Published</small> <strong>Tournament programme</strong> published on {formatEventDate(program.publishedAt, timezone)}
        {program.publishedBy?.name ? ` by ${program.publishedBy.name}` : ''} · on the event page and on every competition page · updates automatically.</p>
      : <>
        <small className="program-publication-state d-block">Not published</small>
        <p className="mb-0"><strong>Tournament programme</strong>
          {deadline ? ` · the website says “published by 12:00 on ${deadline} at the latest”` : ''}
          {missingText ? ` · ${missingText} (they can be published; their groups join after the draw).` : '.'}</p>
      </>}
    {operation.phase === 'pending' && <p className="mb-0" role="status">{published ? 'Unpublishing the programme…' : 'Publishing the programme…'}</p>}
    {pdf.error && <p className="mb-0" role="alert">{pdf.error}</p>}
    {operation.message && <p className="mb-0" role="alert">{operation.message}{operation.phase === 'uncertain' && <button type="button" className="btn btn-link" onClick={operation.verify}>Check status</button>}</p>}
  </div>
  return <>
    {children({ actions, status })}
    <div hidden><ListenerComponent topic="events-response" listenFunction={operation.receive} /></div>
    <Modal isOpen={confirm === 'publish'} toggle={() => setConfirm(null)}>
      <ModalHeader toggle={() => setConfirm(null)}>Publish the tournament programme?</ModalHeader>
      <ModalBody>
        <p>One PDF for the whole tournament — hours, match formats, groups and awards of every competition — on the event page and on every
          competition page. It updates automatically after any change; you can unpublish it at any time.</p>
        {missing.length > 0 && <div className="alert alert-warning mb-0"><strong>{competitionCount(missing.length)} no draw yet:</strong> {missing.join(', ')}.
          They are published with their hours and formats; their groups join after the draw.</div>}
      </ModalBody>
      <ModalFooter><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="primary" onClick={() => run(true)}>Publish programme</Button></ModalFooter>
    </Modal>
    <Modal isOpen={confirm === 'unpublish'} toggle={() => setConfirm(null)}>
      <ModalHeader toggle={() => setConfirm(null)}>Unpublish the tournament programme?</ModalHeader>
      <ModalBody>The programme leaves the event page and the competition pages. Families see the message with the Friday 12:00 deadline again; you
        can publish it again at any time.</ModalBody>
      <ModalFooter><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="danger" onClick={() => run(false)}>Unpublish programme</Button></ModalFooter>
    </Modal>
  </>
}
